/**
 * QuizFlashcards — shared viewer for a multiple-choice flashcard quiz.
 *
 * Used in three places:
 *   - VideoPlayer (after the deck plays through),
 *   - NotesAssignmentView (after generating notes),
 *   - StudySupportSection (when a child / parent opens a note).
 *
 * Keeping the renderer in one place ensures the on-screen flashcard
 * UX stays identical regardless of where the quiz lives (lesson kit
 * deck, standalone video, or notes).
 *
 * Question order and each question's option order are shuffled on load
 * (and re-shuffled on Restart) so repeat attempts don't just repeat the
 * same order — that invites memorizing "the 2nd option" instead of the
 * actual concept.
 *
 * Props:
 *   - quiz: { questions: [{ question_text, options[4], correct_answer_index, explanation, diagram? }] }
 *           Also accepts a bare questions[] array for convenience.
 *           diagram, when present, is {type: "svg", code: "<svg>...</svg>"}.
 *   - onClose: optional close handler (renders an × in the header).
 *   - onReplayDeck: optional handler — shown only when defined (used by VideoPlayer).
 *   - onComplete: optional callback, fired once with {score, total} each time
 *     the deck is finished (including after a Restart) — callers use this to
 *     persist the attempt; this component has no notion of "who"/"which note".
 *     Never fires in previewMode (there's no real attempt to record).
 *   - previewMode: for the creator reviewing a quiz they just generated or
 *     assigned, not a learner taking it. Every card shows the correct answer
 *     revealed immediately (no click needed) and "Next" is always enabled,
 *     so the creator can flip through and check the content without being
 *     forced to "attempt" (and implicitly get graded on) their own quiz.
 *   - className: optional extra container classes.
 */
import React, { useEffect, useMemo, useState } from 'react';
import KaTeXContent from '../common/KaTeXContent';
import { normalizeSvgForDisplay, sanitizeSvgMarkup } from '../../utils/diagramEditor';

const shuffleArray = (arr) => {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Shuffles question order and, within each question, option order —
// remapping correct_answer_index to the option's new position so scoring
// still works.
const shuffleQuiz = (questions) => shuffleArray(
  (questions || []).map((q) => {
    const options = Array.isArray(q.options) ? q.options : [];
    const order = shuffleArray(options.map((_, i) => i));
    return {
      ...q,
      options: order.map((i) => options[i]),
      correct_answer_index: order.indexOf(q.correct_answer_index),
    };
  })
);

// Strips markdown emphasis and LaTeX delimiters (keeping the inner text)
// so the Web Speech API reads words instead of literal "asterisk" / "dollar
// sign" noise. Not a full LaTeX-to-speech translator — complex math will
// still sound rough — but it keeps ordinary sentences clean.
const stripForSpeech = (text) => String(text || '')
  .replace(/\$\$([\s\S]*?)\$\$/g, '$1')
  .replace(/\\\[([\s\S]*?)\\\]/g, '$1')
  .replace(/\\\(([\s\S]*?)\\\)/g, '$1')
  .replace(/\$([^$]*?)\$/g, '$1')
  .replace(/\*\*([^*]+?)\*\*/g, '$1')
  .replace(/\*([^*]+?)\*/g, '$1')
  .replace(/__([^_]+?)__/g, '$1')
  .replace(/<[^>]+>/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const speechSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

export default function QuizFlashcards({ quiz, questions: questionsProp, onClose, onReplayDeck, onComplete, previewMode = false, className = '' }) {
  const rawQuestions = Array.isArray(questionsProp)
    ? questionsProp
    : (quiz?.questions || []);

  // shuffleSeed forces a fresh shuffle on Restart; the JSON key otherwise
  // keeps the same shuffled order across re-renders of an in-progress quiz
  // (rawQuestions is a new array reference most renders, but its contents —
  // and therefore this key — don't change mid-attempt).
  const [shuffleSeed, setShuffleSeed] = useState(0);
  const questions = useMemo(
    () => shuffleQuiz(rawQuestions),
    [JSON.stringify(rawQuestions), shuffleSeed]
  );

  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const q = questions[index];
  const total = questions.length;
  // In preview mode every card is "revealed" from the moment it's shown —
  // the creator is checking content, not answering questions.
  const revealed = previewMode || picked != null;
  const isCorrect = !!questions.length && picked === q.correct_answer_index;
  const isLast = index === total - 1;
  const finished = !!questions.length && isLast && revealed;

  const diagramSvg = useMemo(() => {
    const code = q?.diagram?.code;
    if (!code) return '';
    return sanitizeSvgMarkup(normalizeSvgForDisplay(code));
  }, [q]);

  // Fires onComplete exactly once per finished attempt (including repeat
  // attempts after Restart, since finished goes false→true again). Never
  // fires in previewMode — there's no real attempt to record.
  useEffect(() => {
    if (finished && onComplete && !previewMode) {
      onComplete({ score, total });
    }
  }, [finished]);

  // Stop any in-progress speech when the card changes or the component
  // unmounts, so audio never plays over the wrong question.
  useEffect(() => {
    if (speechSupported) window.speechSynthesis.cancel();
    setIsSpeaking(false);
  }, [index, picked]);
  useEffect(() => () => {
    if (speechSupported) window.speechSynthesis.cancel();
  }, []);

  if (!questions.length) return null;

  const pick = (i) => {
    if (previewMode || picked != null) return;
    setPicked(i);
    if (i === q.correct_answer_index) setScore((s) => s + 1);
  };

  const next = () => {
    setPicked(null);
    if (!isLast) setIndex(index + 1);
  };

  const restart = () => {
    setPicked(null);
    setIndex(0);
    setScore(0);
    setShuffleSeed((s) => s + 1);
  };

  const readAloud = () => {
    if (!speechSupported) return;
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }
    const parts = [q.question_text];
    if (diagramSvg) parts.push('This question has a diagram — take a look above.');
    if (!revealed) {
      parts.push((q.options || [])
        .map((opt, i) => `Option ${String.fromCharCode(65 + i)}: ${opt}.`)
        .join(' '));
    } else if (previewMode) {
      const correctOption = q.options?.[q.correct_answer_index];
      if (correctOption) parts.push(`Correct answer: ${correctOption}.`);
      if (q.explanation) parts.push(q.explanation);
    } else {
      parts.push(isCorrect ? 'Correct!' : 'Not quite.');
      if (q.explanation) parts.push(q.explanation);
    }
    const utterance = new SpeechSynthesisUtterance(stripForSpeech(parts.filter(Boolean).join(' ')));
    utterance.rate = 0.95;
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
    setIsSpeaking(true);
  };

  const closeAndStop = () => {
    if (speechSupported) window.speechSynthesis.cancel();
    onClose?.();
  };

  const scorePercent = total ? Math.round((score / total) * 100) : 0;
  const scoreEmoji = scorePercent === 100 ? '🌟' : scorePercent >= 70 ? '🎉' : scorePercent >= 40 ? '👍' : '💪';
  const scoreMessage = scorePercent === 100
    ? 'Perfect score! You nailed every card.'
    : scorePercent >= 70
      ? 'Great job — you really know this!'
      : scorePercent >= 40
        ? 'Good effort! A little more practice will help.'
        : 'Keep practicing — you\'ll get there!';

  return (
    <div className={`bg-gradient-to-br from-indigo-900 via-purple-800 to-fuchsia-800 p-6 sm:p-10 flex flex-col gap-4 text-white rounded-2xl ${className}`}>
      <div className="flex items-center justify-between text-xs opacity-80">
        <span className="flex items-center gap-2">
          <span>Quiz · Card {index + 1} / {total}</span>
          {previewMode && (
            <span className="text-[10px] font-semibold uppercase tracking-wide bg-white/15 rounded-full px-2 py-0.5">
              👁️ Preview
            </span>
          )}
        </span>
        {onClose && (
          <button onClick={closeAndStop} aria-label="Close" className="text-white/80 hover:text-white text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-white/15 transition-all"><svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg></button>
        )}
      </div>

      <div className="h-1.5 w-full rounded-full bg-white/15 overflow-hidden" role="progressbar" aria-valuenow={index + 1} aria-valuemin={1} aria-valuemax={total}>
        <div
          className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-sky-400 transition-all duration-300"
          style={{ width: `${((index + (picked != null ? 1 : 0)) / total) * 100}%` }}
        />
      </div>

      <div className="flex items-start justify-between gap-3">
        <div className="text-sm sm:text-lg font-semibold leading-snug">
          <KaTeXContent content={q.question_text} className="inline" />
        </div>
        {speechSupported && (
          <button
            type="button"
            onClick={readAloud}
            title={isSpeaking ? 'Stop reading' : 'Read this card aloud'}
            aria-label={isSpeaking ? 'Stop reading' : 'Read this card aloud'}
            className={`shrink-0 rounded-full p-2 transition-colors ${isSpeaking ? 'bg-white text-purple-900' : 'bg-white/10 hover:bg-white/20'}`}
          >
            {isSpeaking ? '⏹️' : '🔊'}
          </button>
        )}
      </div>

      {diagramSvg && (
        <div className="diagram-svg-box bg-white rounded-xl p-3 mx-auto max-h-64 w-full">
          <div dangerouslySetInnerHTML={{ __html: diagramSvg }} />
        </div>
      )}

      <ul className="grid grid-cols-1 gap-2">
        {(q.options || []).map((opt, i) => {
          const isPicked = !previewMode && picked === i;
          const isAnswer = revealed && i === q.correct_answer_index;
          const tone = !revealed
            ? 'bg-white/10 hover:bg-white/20'
            : isAnswer
              ? 'bg-emerald-500/80'
              : isPicked
                ? 'bg-rose-500/80'
                : 'bg-white/5';
          return (
            <li key={i}>
              <button
                type="button"
                disabled={revealed}
                onClick={() => pick(i)}
                className={`w-full text-left rounded-xl px-3 py-2 sm:px-4 sm:py-3 ${tone} transition-colors disabled:cursor-default flex items-center gap-2`}
              >
                <span className="font-semibold text-sm sm:text-lg">{String.fromCharCode(65 + i)}.</span>
                <KaTeXContent content={opt} className="inline flex-1 text-sm sm:text-lg" />
                {isAnswer && <span aria-hidden="true" className="text-sm sm:text-lg">✅</span>}
                {isPicked && !isAnswer && <span aria-hidden="true" className="text-sm sm:text-lg">❌</span>}
              </button>
            </li>
          );
        })}
      </ul>
      {revealed && (
        <div className="bg-black/30 rounded-xl p-3 text-sm">
          <div className={`font-semibold ${previewMode || isCorrect ? 'text-emerald-200' : 'text-rose-200'}`}>
            {previewMode ? '✅ Correct answer' : (isCorrect ? '✓ Correct!' : '✗ Not quite.')}
          </div>
          {q.explanation && (
            <div className="text-white/80 mt-1">
              <KaTeXContent content={q.explanation} className="inline" />
            </div>
          )}
        </div>
      )}

      {finished && !previewMode && (
        <div className="bg-white/10 rounded-xl p-4 text-center">
          <div className="text-3xl mb-1" aria-hidden="true">{scoreEmoji}</div>
          <div className="text-lg font-bold">You scored {score} / {total}</div>
          <div className="text-sm text-white/80 mt-1">{scoreMessage}</div>
        </div>
      )}
      {finished && previewMode && (
        <div className="bg-white/10 rounded-xl p-4 text-center">
          <div className="text-3xl mb-1" aria-hidden="true">👀</div>
          <div className="text-lg font-bold">You've previewed all {total} cards</div>
          <div className="text-sm text-white/80 mt-1">Looks good? Assign it, or edit any card that needs a tweak.</div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 mt-1">
        {onReplayDeck && (
          <button
            type="button"
            onClick={onReplayDeck}
            className="text-xs px-3 py-1.5 rounded-full border border-white/30 hover:border-white/60"
          >
            Replay deck
          </button>
        )}
        {!isLast && (
          <button
            type="button"
            onClick={next}
            disabled={!revealed}
            className="text-sm px-4 py-1.5 rounded-full bg-white text-gray-900 font-semibold disabled:opacity-40"
          >
            Next →
          </button>
        )}
        {finished && (
          <>
            <button
              type="button"
              onClick={restart}
              className="text-xs px-3 py-1.5 rounded-full border border-white/30 hover:border-white/60"
            >
              Restart
            </button>
            {onClose && (
              <button
                type="button"
                onClick={closeAndStop}
                className="text-sm px-4 py-1.5 rounded-full bg-white text-gray-900 font-semibold"
              >
                Done
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
